import { useCallback, useMemo, useRef } from "react";
import type { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { ErrorDisplayUnsavedItems } from "@ryde/controls";
import {
  useBookingsBulkActions,
  useConfirmDirtyField,
  useDynamicFormsFetch,
} from "@ryde/redux";
import {
  BOOKING_BULK_CREATE_PANEL_LABEL_KEYS,
  bookingBulkCreateStateFromPollingState,
  bookingsPollingStateAtom,
  bulkApprovalBookingConflictsAtom,
  bulkApprovalBookingConflictsUpdatedAtom,
  BulkCreateBookingState,
  bulkCreateBookingStateAtom,
  currentBulkSetBookingsTokenAtom,
  defaultBookingsPollingState,
  errors,
  isBulkBookingSetFinishSelector,
  Log,
} from "@ryde/shared";

import { useBookingsPollingHandlingActions } from "../../../../../services";
import type { IUseBulkBookingFlowArguments } from "../bulk-bookings.models";
import { resolveCancelLabel, resolveSaveLabel } from "../helpers";

/**
 * The form -> preview -> commit machine every bulk bookings panel runs on. Only the payload it builds
 * and the init call it emits differ per panel; the state atoms, labels, close guard, refresh and
 * abort are one implementation so the panels cannot drift apart.
 */
export const useBulkBookingFlow = <TForm extends FieldValues, TPayload>({
  onCloseDrawer,
  getPayload,
  startPolling,
  labelKeys = BOOKING_BULK_CREATE_PANEL_LABEL_KEYS,
  isDirty,
  trigger,
}: IUseBulkBookingFlowArguments<TForm, TPayload>) => {
  const { t } = useTranslation();
  const { cleanupPreviousProductionData } = useBookingsBulkActions();
  const { fetchTableRowsServerFiltered } = useDynamicFormsFetch(
    "booking",
    "bookings",
  );
  const [bulkCreateBookingState, setBulkCreateBookingState] = useRecoilState(
    bulkCreateBookingStateAtom,
  );
  const isBulkBookingSetFinish = useRecoilValue(isBulkBookingSetFinishSelector);
  const { isLoading: isBulkBookingSetProceeding } = useRecoilValue(
    bookingBulkCreateStateFromPollingState,
  );

  const setBookingsPollingState = useSetRecoilState(bookingsPollingStateAtom);

  const isPreviewStep =
    bulkCreateBookingState === BulkCreateBookingState.IsFirstPreview ||
    bulkCreateBookingState === BulkCreateBookingState.IsPreview;
  const isBulkBookingSetStartedRef = useRef(false);
  const { abortBookingCreateHandler } = useBookingsPollingHandlingActions();
  const setBulkApprovalBookingConflictsUpdated = useSetRecoilState(
    bulkApprovalBookingConflictsUpdatedAtom,
  );
  const [approvalBookingConflicts, setApprovalBookingConflicts] =
    useRecoilState(bulkApprovalBookingConflictsAtom);
  const approvalBookingConflictsUpdated = useRecoilValue(
    bulkApprovalBookingConflictsUpdatedAtom,
  );

  /** Conflicts still waiting on a manual approval - what the preview subtracts from its total. */
  const unapprovedConflictsCount = useMemo(
    () =>
      Array.from(approvalBookingConflictsUpdated.values())
        .flat()
        .filter(({ approvalReason }) => !approvalReason).length,
    [approvalBookingConflictsUpdated],
  );

  const currentToken = useRecoilValue(currentBulkSetBookingsTokenAtom);
  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: (
      <ErrorDisplayUnsavedItems
        {...(isPreviewStep ? { unsavedItemText: "" } : {})}
      />
    ),
  });
  const isComplete = isPreviewStep && isBulkBookingSetFinish;
  const cancelLabel = resolveCancelLabel({
    isPreviewStep,
    t,
  });
  const saveLabel = resolveSaveLabel({
    isPreviewStep,
    isComplete,
    commitLabelKey: labelKeys.commit,
    t,
  });
  const setBulkBookingStateToForm = useEvent(() => {
    setBookingsPollingState(defaultBookingsPollingState);
    return setBulkCreateBookingState(BulkCreateBookingState.IsForm);
  });

  const resetConflicts = useEvent(() => {
    setBulkBookingStateToForm();
    setApprovalBookingConflicts(new Map());
    setBulkApprovalBookingConflictsUpdated(new Map());
  });

  const handleBulkBookingPanelClose = useEvent(() => {
    isBulkBookingSetStartedRef.current = false;
    cleanupPreviousProductionData();
    resetConflicts();
    onCloseDrawer();
  });

  const onCloseClick = useCallback(
    () =>
      applyIfConfirmed({
        isDirty: isPreviewStep ? !isComplete : isDirty,
      })((): void => {
        handleBulkBookingPanelClose();
      })(),
    [
      applyIfConfirmed,
      handleBulkBookingPanelClose,
      isComplete,
      isDirty,
      isPreviewStep,
    ],
  );

  const onCancelClick = (): void => {
    isBulkBookingSetStartedRef.current = false;

    if (!isPreviewStep) {
      onCloseClick();
    }

    resetConflicts();
  };

  const initPolling = useCallback(
    async (isPreview: boolean): Promise<void> => {
      startPolling(
        getPayload({ isPreview, approvalConflicts: approvalBookingConflicts }),
      );
    },
    [approvalBookingConflicts, getPayload, startPolling],
  );

  const completeHandler = useEvent(async (): Promise<void> => {
    onCloseClick();
  });

  const previewStepHandler = useEvent((): void => {
    Log.count("Bulk booking preview step handler called.");
    setBulkApprovalBookingConflictsUpdated(new Map());
    initPolling(false);
  });

  const refreshHandler = useEvent(async (): Promise<void> => {
    Log.count("Bulk booking preview step refresh handler called.");

    setBulkCreateBookingState(BulkCreateBookingState.IsPreview);
    setBulkApprovalBookingConflictsUpdated(new Map());
    initPolling(true);
  });

  const checkBookingsForConflictsHandler = useEvent(async (): Promise<void> => {
    const isValidForm = await trigger();

    if (!isValidForm) {
      return;
    }

    await initPolling(true);
    setBulkCreateBookingState(BulkCreateBookingState.IsFirstPreview);
  });

  const onSaveClick = useCallback(async (): Promise<void> => {
    if (isComplete) {
      completeHandler();
      Log.debug("[CASE 1] Complete step");
      return;
    }

    if (
      isPreviewStep &&
      !isBulkBookingSetProceeding &&
      !isBulkBookingSetStartedRef.current
    ) {
      isBulkBookingSetStartedRef.current = true;
      Log.debug("[CASE 2] Preview step done, start creating booking");
      previewStepHandler();
      return;
    }

    if (!isPreviewStep && !isBulkBookingSetStartedRef.current) {
      checkBookingsForConflictsHandler();
      Log.debug(`[CASE 3] Starting checking conflicts and validation`);
      return;
    }

    Log.log(
      `[CASE 4]: Bulk booking state not handled case: isPreview=${isPreviewStep}, isBulkBookingSetProceed=${isBulkBookingSetProceeding}`,
    );
  }, [
    checkBookingsForConflictsHandler,
    completeHandler,
    isBulkBookingSetProceeding,
    isComplete,
    isPreviewStep,
    previewStepHandler,
  ]);

  const fetchBookings = useEvent((): void => {
    fetchTableRowsServerFiltered();
  });

  const abortHandler = useEvent(async () => {
    if (!currentToken) {
      Log.warnAndToast(errors.produce.missingToken);
      return;
    }

    abortBookingCreateHandler({ token: currentToken });
  });

  return {
    onCloseClick,
    saveLabel,
    onSaveClick,
    cancelLabel,
    onCancelClick,
    isPreviewStep,
    refreshHandler,
    abortHandler,
    setBulkBookingStateToForm,
    fetchBookings,
    approvalBookingConflicts,
    unapprovedConflictsCount,
    labelKeys,
  };
};
